import type { JSX } from 'react';

import { AiCredentialsNote } from '@/components/domain/AiCredentialsNote';
import { AnimatedDemo } from '@/components/domain/AnimatedDemo';
import { AiPracticeGrid } from '@/components/domain/AiPracticeGrid';
import { CapabilityCard } from '@/components/domain/CapabilityCard';
import { CaseStudyCard } from '@/components/domain/CaseStudyCard';
import { ExperienceTimeline } from '@/components/domain/ExperienceTimeline';
import { Hero } from '@/components/domain/Hero';
import { LeadershipPanel } from '@/components/domain/LeadershipPanel';
import { ProjectCard } from '@/components/domain/ProjectCard';
import { SkillsMatrix } from '@/components/domain/SkillsMatrix';
import { Seo } from '@/components/Seo';
import { ButtonLink } from '@/components/ui/Button';
import { Section, SectionHeading } from '@/components/ui/Section';
import { aiIntro, aiPracticeAreas } from '@/content/ai';
import { terminalDemo } from '@/content/media';
import { caseStudies } from '@/content/caseStudies';
import { experienceRoles } from '@/content/experience';
import { capabilities, leadershipPoints } from '@/content/profile';
import { personalProjects } from '@/content/projects';
import { skillGroups } from '@/content/skills';

export function HomePage(): JSX.Element {
  return (
    <>
      <Seo />

      <Hero />

      <Section id="demo" labelledBy="demo-heading">
        <SectionHeading
          id="demo-heading"
          eyebrow="In the terminal"
          title="Interfaces are not only for browsers"
          description="A text-mode dashboard: the same problems as any data-heavy UI — layout, refresh, legibility — with far tighter constraints."
        />
        <div className="mt-10">
          <AnimatedDemo demo={terminalDemo} />
        </div>
      </Section>

      <Section id="capabilities" tone="muted" labelledBy="capabilities-heading">
        <SectionHeading
          id="capabilities-heading"
          eyebrow="What I do"
          title="Four areas I am relied on for"
          description="The work below recurs across every product I have delivered, from public safety dashboards to AI tooling."
        />
        <ul className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {capabilities.map((capability) => (
            <CapabilityCard key={capability.id} capability={capability} />
          ))}
        </ul>
      </Section>

      <Section id="ai" labelledBy="ai-heading">
        <SectionHeading
          id="ai-heading"
          eyebrow="Applied AI"
          title="Working with AI, in production"
          description={aiIntro}
          actions={
            <ButtonLink to="/case-studies/ibm-ai-code-transformation-platform" variant="secondary">
              The AI platform case study
            </ButtonLink>
          }
        />
        <div className="mt-10">
          <AiPracticeGrid areas={aiPracticeAreas} />
        </div>
        <div className="mt-10">
          <AiCredentialsNote />
        </div>
      </Section>

      <Section id="case-studies" tone="muted" labelledBy="case-studies-heading">
        <SectionHeading
          id="case-studies-heading"
          eyebrow="Selected work"
          title="Engineering case studies"
          description="Four products, each with a different frontend problem at its centre."
          actions={
            <ButtonLink to="/case-studies" variant="secondary">
              All case studies
            </ButtonLink>
          }
        />
        <ul className="mt-10 grid gap-5 md:grid-cols-2">
          {caseStudies.map((caseStudy) => (
            <CaseStudyCard key={caseStudy.slug} caseStudy={caseStudy} />
          ))}
        </ul>
      </Section>

      <Section id="projects" labelledBy="projects-heading">
        <SectionHeading
          id="projects-heading"
          eyebrow="Built independently"
          title="Personal projects"
          description="Work outside client and employer projects — publicly available, so you can try it rather than take my word for it."
        />
        <ul className="mt-10 grid gap-5 lg:grid-cols-2">
          {personalProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </ul>
      </Section>

      <Section id="experience" tone="muted" labelledBy="experience-heading">
        <SectionHeading
          id="experience-heading"
          eyebrow="Career"
          title="From freelance in 2006 to enterprise AI tooling"
          description="Twenty years of software development, the last decade focused on enterprise React and TypeScript."
          actions={
            <ButtonLink to="/experience" variant="secondary">
              Full timeline
            </ButtonLink>
          }
        />
        <div className="mt-10">
          <ExperienceTimeline roles={experienceRoles} variant="compact" />
        </div>
      </Section>

      <Section id="skills" labelledBy="skills-heading">
        <SectionHeading
          id="skills-heading"
          eyebrow="Toolkit"
          title="Skills, grouped by what they are for"
          description="Grouped by capability rather than listed as a keyword cloud."
        />
        <div className="mt-10">
          <SkillsMatrix groups={skillGroups} />
        </div>
      </Section>

      <Section id="leadership" tone="muted" labelledBy="leadership-heading">
        <SectionHeading
          id="leadership-heading"
          eyebrow="Leadership"
          title="Technical leadership"
          description="Alongside hands-on delivery, I have led frontend teams and owned the standards they work to."
        />
        <div className="mt-10">
          <LeadershipPanel points={leadershipPoints} />
        </div>
      </Section>

      <Section id="contact-cta" labelledBy="contact-cta-heading">
        <div className="rounded-lg border border-line bg-surface p-8 sm:p-12">
          <h2 id="contact-cta-heading" className="max-w-2xl text-2xl sm:text-3xl">
            Have a senior frontend, React, or full-stack opportunity?
          </h2>
          <p className="mt-4 max-w-2xl text-muted">
            Send a message and include the role, location model, and expected start date.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ButtonLink to="/contact" size="lg">
              Send a message
            </ButtonLink>
            <ButtonLink to="/cv" size="lg" variant="secondary">
              View the CV
            </ButtonLink>
          </div>
        </div>
      </Section>
    </>
  );
}
